import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEventBus } from '../../../../hooks/use-event-bus';
import { EventFormsDialog } from '../../../../types/event-bus';
import { CenterContent, Dialog, Select, SelectPatientContainer } from './styles';

import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import FormsService from '../../../../services/forms-service/forms-service';
import { PatientDto } from '../../../../services/forms-service/forms-service-types';

import { MenuItem } from '@mui/material';
import { CircularProgress } from '../../../shared/audio-player/styles';

type ActDisplayModalProps = {
  actionType: EventFormsDialog['data']['type'] | 'upload-form';
  publish: (event: any) => void;
  publishToIframes: (event: any) => void;
  patient: PatientDto;
};

export const actDisplayModal = ({ actionType, publish, publishToIframes, patient }: ActDisplayModalProps) => {
  if (actionType === 'upload-form') {
    publish({
      messageType: 'navigate',
      data: {
        pathname: `/forms-frame/assignments/index`,
        search: `?uploadDocumentPatientId=${patient.id}&forceFrameActivation=true`,
        crossFrame: true,
        replaceState: false
      }
    });

    window.setTimeout(() => {
      publishToIframes({
        messageType: 'frame-activate',
        data: {
          frameName: 'forms'
        }
      });
    }, 500);
    return;
  }

  publish({
    messageType: 'formsDialog',
    data: {
      type: actionType,
      patient,
      patientId: patient.id,
      locationId: patient.location?.id
    } as any
  });
};

const actionToTitle = (actionType: string | null) => {
  switch (actionType) {
    case 'add-form':
      return 'Add Form From Library';
    case 'upload-form':
      return 'Upload Document';
    case 'scan-qr-code':
      return 'Scan QR Code';
    case 'send-email-link':
      return 'Send Email Link';
    case 'send-sms-link':
      return 'Send Text Link';
    case 'remove-all-assigned-forms':
      return 'Remove All Assigned Forms';
    case 'fill-on-this-device':
      return 'Fill On This Device';
    default:
      return 'Select Patient';
  }
};

export default function ModalSelectPatient() {
  const [open, setOpen] = useState(false);
  const [actionType, setActionType] = useState<EventFormsDialog['data']['type'] | null>(null);
  const [patients, setPatients] = useState<PatientDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<string | null>(null);

  const { config } = useConfig();
  const oidc = useOidcWithAdmin();

  const formsService = useMemo(() => FormsService.getInstance(config, oidc.oidcService), [config, oidc]);

  const { listen, publish, publishToIframes } = useEventBus();

  const fetchPatients = useCallback(
    async (patientId: number, actionType: EventFormsDialog['data']['type']) => {
      setIsLoading(true);
      try {
        const patients = await formsService.getAllForms(patientId);

        // if type is add-form or upload-form, all patients are available
        const patientsToBeDisplayed = ['add-form', 'upload-form'].includes(actionType)
          ? patients.patients
          : patients.patients.filter((patient) => patient.formAssignments?.length);

        setPatients(patientsToBeDisplayed);
        setSelectedPatient(String(patientsToBeDisplayed[0].id));

        if (patientsToBeDisplayed.length === 1) {
          setOpen(false);
          next(patients.patients[0], actionType);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [formsService]
  );

  useEffect(() => {
    return listen('formsDialog', (event: any) => {
      const data = event.data as {
        type: string;
        patientId: number;
        actionType: EventFormsDialog['data']['type'];
      };

      if (data.type === 'select-patient') {
        setPatients([]);
        setActionType(data.actionType);
        setOpen(true);
        fetchPatients(data.patientId, data.actionType);
      }
    });
  }, [listen, fetchPatients]);

  const handleClose = () => {
    setOpen(false);
  };

  const next = (patient: PatientDto, actionType: EventFormsDialog['data']['type']) => {
    setOpen(false);
    actDisplayModal({
      publish,
      publishToIframes,
      patient,
      actionType
    });
  };

  const handleNext = () => {
    if (!selectedPatient || !actionType) {
      return;
    }

    const patient = patients.find((patient) => patient.id === Number(selectedPatient)) as PatientDto;
    next(patient, actionType);
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      {isLoading ? (
        <DialogContent>
          <CenterContent sx={{ minHeight: '120px' }}>
            <CircularProgress />
          </CenterContent>
        </DialogContent>
      ) : (
        <>
          <DialogTitle>Choose Patient - {actionToTitle(actionType)}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please select the family member for this action.
              <SelectPatientContainer>
                Patient:{' '}
                <Select value={selectedPatient} onChange={(e) => setSelectedPatient(e.target.value as string)}>
                  {patients.map((patient) => (
                    <MenuItem key={`patient-selection-${patient.id}`} value={patient.id}>
                      {patient.firstName} {patient.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </SelectPatientContainer>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button sx={{ backgroundColor: '#EBF3FA' }} onClick={handleClose}>
              Cancel
            </Button>
            <Button sx={{ backgroundColor: '#0675DD' }} onClick={handleNext} variant="contained">
              Next
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
